import { ref } from 'vue'
import { DateTime } from 'luxon'

import {
  DATE_FORMATS,
  ISO_DATE_SHORT_FORMAT,
  LEAP_YEAR_EXAMPLE,
  PASTE_DATE_FORMATS,
  TIME_FORMAT,
} from './utils/const'
import { DateTimeObject, DateTimeOptions } from './utils/types'

export const dateFormat = ref(DATE_FORMATS[0])

export function isShortDate(value: string) {
  return ISO_DATE_SHORT_FORMAT.test(value)
}

export function isDate(value?: string) {
  return value && DateTime.fromISO(value).isValid
}

const mapDateFormat = (data: string) =>
  data.replace('yyyy', 'yy').replace('MM', 'M').replace('dd', 'd')

export const stringToDateTime = (data?: string | null, formatted = false) => {
  if (!data) return
  if (formatted) {
    return DateTime.fromFormat(data, mapDateFormat(dateFormat.value), {
      zone: 'utc',
    })
  }
  const result = DateTime.fromISO(data, isShortDate(data) ? { zone: 'utc' } : {})
  return result.isValid ? result : undefined
}

export const dateTimeToString = (
  date?: DateTime | null,
  options?: DateTimeOptions,
) => {
  if (!(date instanceof DateTime)) {
    return ''
  }
  if (options?.iso) {
    return options.withTime ? date.toISO() : date.toISODate()
  } else if (options?.relative) {
    const today = DateTime.now()
    let stringDate
    if (today.hasSame(date, 'day')) {
      stringDate = 'today'
    }
    const yesterday = today.plus({ days: -1 })
    if (yesterday.hasSame(date, 'day')) {
      stringDate = 'yesterday'
    }
    if (!stringDate) {
      const relativeDateFormat =
        'dd MMM' + (DateTime.now().hasSame(date, 'year') ? '' : ' yyyy')
      stringDate = date.toFormat(relativeDateFormat)
    }
    if (options?.withTime) {
      stringDate += date.toFormat(` | ${TIME_FORMAT}`)
    }
    return stringDate
  } else if (options?.customFormat) {
    return date.toFormat(options?.customFormat)
  } else {
    return options?.withTime
      ? date.toFormat(`${dateFormat.value} ${TIME_FORMAT}`)
      : date.toFormat(dateFormat.value)
  }
}

export const stringToLocalDateString = (
  data?: string | null,
  options?: DateTimeOptions,
) => {
  return dateTimeToString(stringToDateTime(data), options)
}

export const objectDateTimeToString = (
  data?: DateTimeObject,
  options?: DateTimeOptions,
) => {
  return dateTimeToString(
    data && DateTime.fromObject(data, { zone: 'utc' }),
    options,
  )
}

export function getCurrentDate(): DateTime
export function getCurrentDate(options: DateTimeOptions): string
export function getCurrentDate(options?: DateTimeOptions) {
  const date = DateTime.now()
  if (options) {
    return dateTimeToString(date, { iso: true, ...options })
  } else {
    return DateTime.utc(date.year, date.month, date.day)
  }
}

export const isTheSameDates = (dateOne?: string, dateTwo?: string) => {
  const dateOneObject = stringToDateTime(dateOne)
  const dateTwoObject = stringToDateTime(dateTwo)
  return dateTwoObject && dateOneObject?.hasSame(dateTwoObject, 'day')
}

export const getDaysInMonthCount = (month: number, year?: number | null) => {
  if (!year) year = LEAP_YEAR_EXAMPLE // any leap year works as default
  return new Date(year, month, 0).getDate()
}

export const getLastWeek = (date: DateTime) => {
  return date.startOf('week').plus({ days: -1 })
}

export const getLastMonth = (date: DateTime) => {
  return date.startOf('month').plus({ days: -1 })
}

export const getLastYear = (date: DateTime) => {
  return date.startOf('year').plus({ days: -1 })
}

export const monthDateToName = (value?: string | null) => {
  const date = stringToDateTime(value)
  return `${date?.monthShort}`
}

export const convertDateToISO = (date: string) => {
  return stringToDateTime(date, true)?.toISODate()
}

export const parseDate = (dateText: string): { isoDate: string | null; isAmbiguous: boolean } => {
  const trimmedText = dateText.trim()
  if (!trimmedText) {
    return { isoDate: null, isAmbiguous: false }
  }

  let parsedDate: DateTime = DateTime.fromISO(trimmedText)
  let validList: Record<number, boolean> = {}

  // Try user's preferred date format
  if (!parsedDate.isValid) {
    parsedDate = DateTime.fromFormat(trimmedText, dateFormat.value)
  }

  // Try common paste formats
  if (!parsedDate.isValid) {
    for (const format of PASTE_DATE_FORMATS) {
      const attemptedDate = DateTime.fromFormat(trimmedText, format)
      if (attemptedDate.isValid) {
        validList[attemptedDate.toMillis()] = true
        parsedDate = attemptedDate
      }
    }
  }

  // Try native Date parsing as fallback
  if (!parsedDate.isValid) {
    const nativeDate = new Date(trimmedText)
    if (!isNaN(nativeDate.getTime())) {
      parsedDate = DateTime.fromJSDate(nativeDate)
    }
  }

  if (!parsedDate.isValid) {
    return { isoDate: null, isAmbiguous: false }
  }

  // Check for ambiguous date formats (multiple valid interpretations)
  const isAmbiguous = Object.keys(validList).length > 1

  const isoDate = parsedDate.toISODate()
  return { isoDate, isAmbiguous }
}
